<template>
  <div class="account-settings">
    <div class="settings-title">
      <span class="title-text">
        <el-text
          size="large"
          class="setting-nav-title"
        >
          {{ $t('views.account_center.account_settings') }}
        </el-text>
      </span>
    </div>

    <!-- 头像和昵称部分 -->
    <div class="profile-section">
      <div class="profile-container">
        <UserAvatarBadge
          :size="64"
          :src="$store.getters['user/avatar'] || require('@/assets/img/def_user.png')"
          :subscription-key="$store.getters['user/user_sub']"
        />
        <div class="user-name-input">
          <div class="username-title"> <el-text
            size="default"
          >{{ $t('views.account_center.nickname') }}</el-text></div>
          <div class="nickname-container">
            <div
              v-show="!isEditingName"
              class="nickname-display"
            >
              <span><el-text
                size="default"
              >{{ $store.getters['user/nick_name'] }}</el-text></span>
              <el-icon
                class="edit-icon"
                @click="startEditName"
              ><EditPen /></el-icon>
            </div>
            <el-input
              v-show="isEditingName"
              ref="nicknameInput"
              v-model="nickName"
              :data-placeholder="$t('views.account_center.nickname_placeholder')"
              size="default"
              style="width: 240px"
              maxlength="100"
              @blur="saveNickName"
              @keyup.enter="saveNickName"
            />
          </div>
        </div>
      </div>
      <div class="avatar-add">
        <el-button
          type="primary"
          size="small"
          link
        >
          <label
            class="add-image"
            for="uploads"
          >{{ $t('views.account_center.add_avatar') }}</label>
          <input
            id="uploads"
            type="file"
            style="display: none"
            accept="image/png, image/jpeg, image/gif, image/jpg"
            @change="uploadImg($event)"
          >
        </el-button>
        <div v-if="membershipExpiryText" class="membership-expiry">{{ membershipExpiryText }}</div>
      </div>
    </div>

    <!-- 邮箱地址部分 -->
    <div class="email-section">
      <div class="email-title">
        <div class="email-title-text"><el-text>{{ $t('views.account_center.email_address') }}</el-text></div>
        <span class="email-container"><el-text size="small">{{ $store.getters['user/email'] }}</el-text></span>
      </div>
      <el-button
        @click="handleUpdateEmail"
      >{{ $t('views.account_center.change_email') }}</el-button>
    </div>

    <!-- 设备管理部分 -->
    <div class="device-section">
      <div class="device-header">
        <div class="device-left">
          <div class="decice-title-text"><el-text>{{ $t('views.account_center.device_management') }}</el-text></div>
          <div class="device-tip"><el-text size="small">{{ $t('views.account_center.device_tip') }}</el-text> </div>
        </div>
        <el-button @click="handleLogoutAll">{{ $t('views.account_center.logout_all_devices') }}</el-button>
      </div>
      <!-- 骨架加载表格 -->
      <div
        v-if="isLoading"
        class="table-skeleton"
      >
        <el-skeleton animated>
          <template #template>
            <!-- 表头骨架 -->
            <div class="skeleton-header">
              <el-skeleton-item
                v-for="n in 4"
                :key="'header' + n"
                variant="text"
                class="skeleton-header-item"
              />
            </div>
            <!-- 表格内容骨架 -->
            <div
              v-for="row in 2"
              :key="'row' + row"
              class="skeleton-row"
            >
              <el-skeleton-item
                v-for="col in 4"
                :key="'col' + col"
                variant="text"
                class="skeleton-cell"
              />
            </div>
          </template>
        </el-skeleton>
      </div>
      <!-- 实际表格 -->
      <el-table
        v-else
        :data="deviceList"
        style="width: 100%"
        max-height="190px"
      >
        <!-- 图标列固定宽度 -->
        <el-table-column width="60" align="center">
          <template #default>
            <el-icon size="16"><Monitor /></el-icon>
          </template>
        </el-table-column>

        <!-- 设备名称给较大的 min-width -->
        <el-table-column
          :label="$t('views.account_center.device_name')"
          min-width="280"
        >
          <template #default="{ row }">
            <div class="device-name-container">
              <span>{{ row.device_name }}</span>
              <span
                v-if="isCurrentDevice(row.device_id)"
                class="current-device-tag"
              >
                <el-text
                  type="primary"
                  size="small"
                >{{ $t('views.account_center.current_device') }}</el-text>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- 活跃时间 -->
        <el-table-column
          :label="$t('views.account_center.last_active_time')"
          min-width="220"
        >
          <template #default="{ row }">
            <span v-if="isCurrentDevice(row.device_id)">{{ $t('views.account_center.now') }}</span>
            <span v-else>{{ row.last_login_time }}</span>
          </template>
        </el-table-column>

        <!-- 位置 -->
        <el-table-column
          prop="ip_address"
          :label="$t('views.account_center.location')"
          min-width="200"
        />

        <!-- 操作列固定在右侧 -->
        <el-table-column align="right" width="120">
          <template #default="scope">
            <el-button
              v-if="scope.row.status===0 && !isCurrentDevice(scope.row.device_id)"
              @click="handleLogout(scope.row.device_id)"
            >{{ $t('views.account_center.logout') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 删除账号部分 -->
    <div class="delete-account">
      <el-button
        type="danger"
        link
        @click="handleDeleteAccount"
      >{{ $t('views.account_center.delete_my_account') }}</el-button>
      <div class="delete-tip"><el-text size="small">{{ $t('views.account_center.delete_account_tip') }}</el-text></div>
    </div>
  </div>

  <!-- 头像剪切对话框 -->
  <el-dialog
    v-model="dialogCutImg"
    class="edit-dlg"
    :close-on-click-modal="false"
    width="600px"
    :title="$t('views.account_center.crop_image')"
    append-to-body
    @close="dialogCutImgClose"
  >
    <div class="cut-img-box">
      <el-row>
        <el-col :span="24">
          <i class="el-icon-info" />{{ $t('views.account_center.need_crop_image') }}
        </el-col>
      </el-row>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button
          v-loading="upLoading"
          type="primary"
          @click="submitCutImg()"
        >{{ $t('views.account_center.crop') }}</el-button>
        <el-button
          v-loading="upLoading"
          @click="closeCutImg()"
        >{{ $t('views.account_center.upload_without_crop') }}</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 头像上传对话框 -->
  <el-dialog
    v-model="dialogUploadImg"
    :close-on-click-modal="false"
    width="600px"
    :title="$t('views.account_center.upload_image')"
    append-to-body
  >
    <div class="uploadImgBox">
      <CropperImg
        ref="uploadImgDlg"
        :image="uploadImgData"
        :select-file-name="selectFileName"
        :fixed-number="uploadImgFixedNumber"
        :fixed="fixed"
        :center-box="centerBox"
        @uploadingBase64="uploadingBase64"
        @uploadSucceed="uploadImgSucceed"
      />
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="closeDialogUploadImg()">{{ $t("common.cancel") }}</el-button>
        <el-button
          type="primary"
          @click="submitUploadImg()"
        >{{ $t("common.confirm") }}</el-button>
      </span>
    </template>
  </el-dialog>

  <!--  邮箱表单验证-->
  <el-dialog
    v-model="emailDialogVisible"
    :title="$t('views.account_center.change_email_title')"
    width="520px"
    :close-on-click-modal="false"
  >
    <!-- 第一步：显示当前邮箱 -->
    <div class="dialog-content">
      <div
        v-if="!$store.getters['user/email']"
        class="step1-text"
      >
        <el-text>{{ $t('views.account_center.no_email_tip') }}</el-text>
      </div>
      <div
        v-else
        class="step1-text"
      >
        <el-text>{{ $t('views.account_center.current_email_is') }}</el-text><span class="email-text"><el-text>{{ $store.getters['user/email'] }}</el-text></span>
        <span v-if="!oldEmailCodeSent"><el-text>{{ $t('views.account_center.will_send_code') }}</el-text></span>
        <span v-else-if="oldEmailVerified"><el-text>{{ $t('views.account_center.verification_completed') }}</el-text></span>
        <span v-else />
      </div>
      <!-- 第二步：旧邮箱验证码输入 -->
      <div
        v-if="step >= 2 && !oldEmailVerified && $store.getters['user/email']"
        class="verify-sectionodl"
      >
        <el-input
          v-model="emailForm.oldEmailCode"
          :data-placeholder="$t('views.account_center.enter_verification_code')"
          class="verify-input"
        >
          <template #append>
            <el-button
              :disabled="oldEmailTimer > 0"
              @click="sendOldEmailCode"
            >
              {{ oldEmailTimer > 0 ? $t('views.account_center.retry_later', { newEmailTimer: oldEmailTimer }) : $t('views.account_center.resend') }}
            </el-button>
          </template>
        </el-input>
      </div>

      <!-- 第三步：新邮箱输入 -->
      <div
        v-if="step >= 3"
        class="new-email-section"
      >
        <p>{{ $t('views.account_center.enter_new_email') }}</p>
        <div class="newemail-inputp-content">
          <el-input
            v-model="emailForm.newEmail"
            :data-placeholder="$t('views.account_center.enter_new_email_placeholder')"
          />
        </div>
      </div>

      <!-- 第四步：新邮箱验证码 -->
      <div
        v-if="step >= 4"
        class="verify-section"
      >
        <!-- 添加提示文本 -->
        <p class="verification-tip">{{ $t('views.account_center.verification_tip', { email: emailForm.newEmail }) }}</p>

        <!-- 验证码输入框 -->
        <el-input
          v-model="emailForm.newEmailCode"
          :data-placeholder="$t('views.account_center.enter_new_email_placeholder')"
          class="verify-input"
        >
          <template #append>
            <el-button
              :disabled="newEmailTimer > 0"
              @click="sendNewEmailCode"
            >
              {{ newEmailTimer > 0 ? $t('views.account_center.retry_later', { newEmailTimer }) : $t('views.account_center.resend') }}
            </el-button>
          </template>
        </el-input>
      </div>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button
          type="primary"
          :loading="stepLoading"
          @click="handleStepAction"
        >
          {{ getActionButtonText }}
        </el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 删除账号弹窗-->
  <el-dialog
    v-model="accountDialogVisible"
    :title="$t('views.account_center.delete_account')"
    width="550px"
    :close-on-click-modal="false"
  >
    <div class="dialog-content">
      <p class="warning-text"><el-icon
        color="#FF9900"
        size="large"
      ><Warning /></el-icon><el-text>{{ $t('views.account_center.delete_warning') }}</el-text></p>
      <div class="confirm-instruction-wrapper">
        <span class="confirm-instruction">
          <el-text>{{ $store.getters['user/email']
            ? $t('views.account_center.confirm_by_email')
            : $t('views.account_center.confirm_by_phone') }}</el-text>
        </span>
        <span class="email-text">
          <el-text> {{ $store.getters['user/email'] || $store.getters['user/phone'] }}</el-text>
        </span>
      </div>
      <el-input
        v-model="deleteEmailConfirm"
        type="text"
        :placeholder="$store.getters['user/email'] ? $t('views.account_center.enter_email_confirm') : $t('views.account_center.enter_phone_confirm')"
        style="width: 300px;padding-left: 23px"
      />
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="accountDialogVisible = false">{{ $t("common.cancel") }}</el-button>
        <el-button
          type="danger"
          :loading="deleteAccountLoading"
          :disabled="deleteEmailConfirm !== ($store.getters['user/email'] || $store.getters['user/phone'])"
          @click="confirmDeleteAccount"
        >{{ $t('views.account_center.confirm_delete_account') }}</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import {
  BindNewEmail,
  getEmailResetToken,
  getCurrSubscription,
  getSysEmailCode,
  updateBaseUserInfo,
  addNewEmail,
  deleteAccount
} from '@/api/user'
import {uploadFile} from '@/api/attachment'
import {getUserDevice, logoutAllDevice, logoutDevice} from '@/api/device'
import CropperImg from '@/components/CropperImg/index.vue'
import UserAvatarBadge from '@/components/UserAvatarBadge/index.vue'
import {Warning} from '@element-plus/icons-vue'

export default {
  name: 'AccountSettings',
  components: {
    Warning,
    CropperImg,
    UserAvatarBadge
  },
  data() {
    return {
      deviceList: [],
      nickName: '',
      dialogCutImg: false,
      uploadEvent: {},
      upLoading: false,
      isEditingName: false,
      isSavingNickName: false, // 添加保存标志,防止重复触发
      dialogUploadImg: false,
      uploadImgData: '',
      uploadImgFixedNumber: [100, 100],
      selectFileName: '',
      fixed: true,
      centerBox: false,
      emailDialogVisible: false,
      accountDialogVisible: false,
      deleteEmailConfirm: '',
      deleteAccountLoading: false,
      step: 1,
      stepLoading: false,
      emailForm: {
        oldEmailCode: '', // 旧邮箱验证码
        newEmail: '', // 新邮箱
        newEmailCode: '', // 新邮箱验证码
        resetToken: '' // 重置邮箱的token
      },
      oldEmailVerified: false, // 旧邮箱验证码是否已验证
      oldEmailCodeSent: false, // 是否已发送旧邮箱验证码
      oldEmailTimer: 0, // 旧邮箱验证码倒计时
      oldEmailTimerId: null, // 旧邮箱计时器ID
      newEmailTimer: 0, // 新邮箱计时器ID
      newEmailTimerId: null, // 新邮箱计时器ID
      isLoading: true, // 骨架加载
      membershipExpiryDate: ''
    }
  },
  computed: {
    getActionButtonText() {
      if (!this.$store.getters['user/email']) {
        const textMap = {
          3: this.$t('views.account_center.send_code'),
          4: this.$t('views.account_center.bind_email')
        }
        return textMap[this.step]
      } else {
        const textMap = {
          1: this.$t('views.account_center.send_code'),
          2: this.$t('views.account_center.verify'),
          3: this.$t('views.account_center.send_code'),
          4: this.$t('views.account_center.change_email_action')
        }
        return textMap[this.step]
      }
    },
    membershipExpiryText() {
      if (!this.membershipExpiryDate) return ''
      return this.$t('views.account_center.membership_expires_on', {
        expiryDate: this.formatMembershipExpiry(this.membershipExpiryDate)
      })
    }
  },
  mounted() {
    this.getUserInfoData()
    // 初始化昵称
    this.nickName = this.$store.getters['user/nick_name']
    this.getMembershipExpiry()
    this.getDeviceList()
  },
  beforeUnmount() {
    if (this.oldEmailTimerId) {
      clearInterval(this.oldEmailTimerId)
      this.oldEmailTimerId = null
    }
    if (this.newEmailTimerId) {
      clearInterval(this.newEmailTimerId)
      this.newEmailTimerId = null
    }
  },
  methods: {
    getMembershipExpiry() {
      getCurrSubscription()
        .then(response => {
          const subscription = response?.data
          this.membershipExpiryDate = subscription && !Array.isArray(subscription)
            ? (subscription.expires_at || subscription.ends_at || subscription.next_billing_date || '')
            : ''
        })
        .catch(error => {
          console.error('Failed to load membership expiry', error)
          this.membershipExpiryDate = ''
        })
    },
    formatMembershipExpiry(value) {
      if (!value) return ''
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (match) return `${match[1]}.${match[2]}.${match[3]}`
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}.${month}.${day}`
    },
    isCurrentDevice(deviceId) {
      // 添加空值判断
      const currentDeviceId = localStorage.getItem('X-Device-ID') || ''
      return deviceId === currentDeviceId
    },
    startEditName() {
      this.isEditingName = true
      this.$nextTick(() => {
        this.$refs.nicknameInput.focus()
      })
    },
    saveNickName() {
      // 防止重复触发
      if (this.isSavingNickName) {
        return
      }
      
      if (!this.nickName || !this.nickName.trim()) {
        this.$message.warning(this.$t('views.account_center.nickname_empty'))
        this.nickName = this.$store.getters['user/nick_name']
        this.isEditingName = false
        return
      }

      if (this.nickName === this.$store.getters['user/nick_name']) {
        this.isEditingName = false
        return
      }

      this.isSavingNickName = true
      
      // 更新昵称
      this.updateBaseUserInfoData({ nick_name: this.nickName })
      this.isEditingName = false
      
      setTimeout(() => {
        this.isSavingNickName = false
      }, 300)
    },
    // 上传头像相关方法
    uploadImg(e) {
      this.uploadEvent = e
      if (e.target.files.length === 0) {
        return false
      }
      for (let i = 0; i < e.target.files.length; i++) {
        if (!/\.(gif|jpg|jpeg|png|bmp|GIF|JPG|PNG)$/.test(e.target.files[i].name)) {
          this.$message({
            message: this.$t('views.account_center.image_type_error'),
            type: 'error'
          })
          return false
        }
        if (e.target.files[i].size > 20 * 1024 * 1024) {
          this.$message({
            message: this.$t('common.single_image_size_no_more_than_50M'),
            type: 'error'
          })
          return false
        }
      }
      if (e.target.files.length === 1) {
        this.submitCutImg()
      } else {
        const files = e.target.files
        this.upLoading = true
        let uploadCount = 0
        for (let i = 0; i < files.length; i++) {
          const formData = new FormData()
          formData.append('file', files[i])
          uploadFile(formData).then(() => {
            uploadCount = uploadCount + 1
            if (uploadCount === files.length) {
              this.upLoading = false
              document.getElementById('uploads').value = ''
            }
          }).catch(() => {
            this.upLoading = false
            document.getElementById('uploads').value = ''
          })
        }
      }
    },

    submitCutImg() {
      const file = this.uploadEvent.target.files[0]
      const reader = new FileReader()
      reader.onload = e => {
        let data
        if (typeof e.target.result === 'object') {
          // 把Array Buffer转化为blob 如果是base64不需要
          data = window.URL.createObjectURL(new Blob([e.target.result]))
        } else {
          data = e.target.result
        }
        this.uploadImgData = data
        this.selectFileName = document.getElementById('uploads').value.match(/\\([^\\^.]+)\.[^\\]*$/)[1]
        document.getElementById('uploads').value = ''
        this.dialogUploadImg = true
        this.dialogCutImgClose()
      }
      reader.readAsArrayBuffer(file)
    },

    dialogCutImgClose() {
      document.getElementById('uploads').value = ''
      this.dialogCutImg = false
    },

    closeCutImg() {
      const file = this.uploadEvent.target.files[0]
      const formData = new FormData()
      formData.append('file', file)
      this.upLoading = true
      uploadFile(formData).then(response => {
        this.upLoading = false
        document.getElementById('uploads').value = ''
        this.uploadEvent = {}
        if (response.data) {
          this.uploadImgSucceed(response.data)
        } else {
          this.$message({
            message: response,
            type: 'error'
          })
        }
        this.dialogCutImgClose()
      }).catch(() => {
        this.upLoading = false
        document.getElementById('uploads').value = ''
        this.dialogCutImgClose()
      })
    },

    uploadImgSucceed(data) {
      if (data) {
        // 更新用户头像
        const avatar = data.file
        this.updateBaseUserInfoData({ avatar })
        // 强制更新界面
        this.$store.commit('user/setAvatar', avatar)
        this.$forceUpdate()
      }
    },

    uploadingBase64(date) {
      this.upLoading = date
    },

    closeDialogUploadImg() {
      this.dialogUploadImg = false
    },

    submitUploadImg() {
      this.$refs['uploadImgDlg'].finish()
      this.closeDialogUploadImg()
    },
    async handleStepAction() {
      this.stepLoading = true
      try {
        switch (this.step) {
          case 1: {
            const sent = await this.sendOldEmailCode()
            if (sent) {
              this.step = 2
            }
            break
          }
          case 2: {
            const verified = await this.verifyOldEmailCode()
            if (verified) {
              this.step = 3
            }
            break
          }
          case 3: {
            if (this.validateNewEmail()) {
              const sent = await this.sendNewEmailCode()
              if (sent) {
                this.step = 4
              }
            }
            break
          }
          case 4: {
            await this.submitEmailChange()
            break
          }
        }
      } finally {
        this.stepLoading = false
      }
    },
    async sendOldEmailCode() {
      if (this.oldEmailTimer > 0) return false

      try {
        // 发送验证码，type=2表示更换邮箱
        await getSysEmailCode({
          email: this.$store.getters['user/email'],
          type: 2
        })

        // 清空输入框内容
        this.emailForm.oldEmailCode = ''
        this.oldEmailCodeSent = true

        // 设置倒计时
        this.oldEmailTimer = 60
        this.startOldEmailTimer()

        this.$message.success(this.$t('views.account_center.code_sent'))
        return true
      } catch (error) {
        return false
      }
    },
    startOldEmailTimer() {
      if (this.oldEmailTimerId) {
        clearInterval(this.oldEmailTimerId)
      }

      this.oldEmailTimerId = setInterval(() => {
        if (this.oldEmailTimer > 0) {
          this.oldEmailTimer--
        } else {
          clearInterval(this.oldEmailTimerId)
          this.oldEmailTimerId = null
        }
      }, 1000)
    },
    async verifyOldEmailCode() {
      if (!this.emailForm.oldEmailCode) {
        this.$message.warning(this.$t('views.account_center.code_required'))
        return false
      }

      try {
        // 调用获取reset_token的接口
        const response = await getEmailResetToken({
          valid_code: this.emailForm.oldEmailCode
        })

        if (response.code === 0 && response.data) {
          // 保存reset_token，用于后续操作
          this.emailForm.resetToken = response.data.reset_token
          this.$message.success(this.$t('views.account_center.verification_completed'))
          this.oldEmailVerified = true
          return true
        } else {
          return false
        }
      } catch (error) {
        return false
      }
    },
    async sendNewEmailCode() {
      if (this.newEmailTimer > 0) return false

      if (!this.validateNewEmail()) {
        return false
      }

      try {
        await getSysEmailCode({
          email: this.emailForm.newEmail,
          type: 2
        })
        // 清空输入框内容
        this.emailForm.newEmailCode = ''

        // 设置倒计时
        this.newEmailTimer = 60
        this.startNewEmailTimer()

        this.$message.success(this.$t('views.account_center.code_sent'))
        return true
      } catch (error) {
        return false
      }
    },
    validateNewEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!this.emailForm.newEmail) {
        this.$message.warning(this.$t('views.account_center.email_required'))
        return false
      }
      if (!emailRegex.test(this.emailForm.newEmail)) {
        this.$message.warning(this.$t('views.account_center.email_invalid'))
        return false
      }
      if (this.emailForm.newEmail === this.$store.getters['user/email']) {
        this.$message.warning(this.$t('views.account_center.email_duplicate'))
        return false
      }
      return true
    },
    startNewEmailTimer() {
      if (this.newEmailTimerId) {
        clearInterval(this.newEmailTimerId)
      }

      this.newEmailTimerId = setInterval(() => {
        if (this.newEmailTimer > 0) {
          this.newEmailTimer--
        } else {
          clearInterval(this.newEmailTimerId)
          this.newEmailTimerId = null
        }
      }, 1000)
    },
    resetEmailForm() {
      this.step = 1
      this.emailForm = {
        oldEmailCode: '',
        newEmail: '',
        newEmailCode: '',
        resetToken: ''
      }
      this.oldEmailCodeSent = false
      this.oldEmailVerified = false

      if (this.oldEmailTimerId) {
        clearInterval(this.oldEmailTimerId)
        this.oldEmailTimerId = null
      }
      if (this.newEmailTimerId) {
        clearInterval(this.newEmailTimerId)
        this.newEmailTimerId = null
      }

      this.oldEmailTimer = 0
      this.newEmailTimer = 0
    },
    async getUserInfoData() {
      // 获取用户信息数据
      // const userInfo = await this.$store.dispatch('user/getUserInfo')
      this.nickName = this.$store.getters['user/nick_name']
    },
    updateBaseUserInfoData(data) {
      const params = {}
      if (data.avatar) {
        params.avatar = data.avatar
      }
      if (data.nick_name) {
        params.nick_name = data.nick_name
      }

      if (params.avatar || params.nick_name) {
        updateBaseUserInfo(params).then(response => {
          this.$message.success(this.$t('views.account_center.save_success'))

          // 更新本地状态
          if (data.nick_name) {
            this.$store.commit('user/setNickName', data.nick_name)
          }
          if (data.avatar) {
            this.$store.commit('user/setAvatar', data.avatar)
          }

          // 获取更新后的用户信息
          this.getUserInfoData()
        }).catch(e => {
          if (data.avatar) {
            this.upLoading = false
            document.getElementById('uploads').value = ''
          }
          // 恢复原昵称
          if (data.nick_name) {
            this.nickName = this.$store.getters['user/nick_name']
          }
        })
      }
    },
    async submitEmailChange() {
      if (!this.emailForm.newEmailCode) {
        this.$message.warning(this.$t('views.account_center.code_required'))
        return false
      }

      try {
        // 检查是否是首次绑定邮箱
        const isFirstBind = !this.$store.getters['user/email']
        let response

        if (isFirstBind) {
          // 首次绑定邮箱，使用 addNewEmail 接口
          response = await addNewEmail({
            email: this.emailForm.newEmail,
            valid_code: this.emailForm.newEmailCode
          })
        } else {
          // 更改现有邮箱，使用 BindNewEmail 接口，需要 reset_token
          const resetToken = this.emailForm.resetToken
          if (!resetToken) {
            this.$message.error(this.$t('views.account_center.token_invalid'))
            this.resetEmailForm()
            this.emailDialogVisible = false
            return false
          }

          response = await BindNewEmail({
            reset_token: resetToken,
            email: this.emailForm.newEmail,
            valid_code: this.emailForm.newEmailCode
          })
        }

        if (response.code === 0) {
          // 更新成功
          this.$message.success(isFirstBind ? this.$t('views.account_center.email_bind_success') : this.$t('views.account_center.email_update_success'))
          this.emailDialogVisible = false
          this.$store.dispatch('user/logout').then(() => {
            this.$router.push({ path: '/login' })
          })
          // 重置表单
          this.resetEmailForm()
          return true
        } else {
          return false
        }
      } catch (error) {
        return false
      }
    },
    handleUpdateEmail() {
      this.resetEmailForm()
      this.emailDialogVisible = true
      if (!this.$store.getters['user/email']) {
        this.step = 3
      } else {
        this.step = 1
      }
    },
    async handleLogoutAll() {
      try {
        const params = {
          except_current: 1
        }
        const logoutall = await logoutAllDevice(params)
        if (logoutall) {
          this.$message.success(this.$t('views.account_center.logout_success'))
          // 刷新设备列表
          this.getDeviceList()
        } else {
          this.$message.error(logoutall.message || this.$t('views.account_center.operation_failed'))
        }
      } catch (error) {
        if (error !== 'cancel') { // 忽略取消操作
          console.error('登出设备失败:', error)
          this.$message.error(error.message || this.$t('views.account_center.operation_failed'))
        }
      }
    },
    async handleLogout(deviceId) {
      try {
        const currentDeviceId = localStorage.getItem('X-Device-ID')
        // 判断是否为当前设备
        if (deviceId === currentDeviceId) {
          // 如果是当前设备，先显示提示
          const loadingMessage = this.$message.info(this.$t('views.account_center.logout_current_device'))
          // 执行登出操作
          await this.$store.dispatch('user/logout')
          loadingMessage.close()
          this.$message.success(this.$t('views.account_center.logout_device_success'))
          this.$router.push('/login')
        } else {
          // 登出其他设备
          const params = { device_id: deviceId }
          const response = await logoutDevice(params)

          if (response && response.code === 0) {
            this.$message.success(this.$t('views.account_center.logout_device_success'))
            // 刷新设备列表
            this.getDeviceList()
          } else {
            this.$message.error(response.message || this.$t('views.account_center.operation_failed'))
          }
        }
      } catch (error) {
        console.error('登出设备失败:', error)
        this.$message.error(error.message || this.$t('views.account_center.logout_failed'))
      }
    },
    handleDeleteAccount() {
      this.deleteEmailConfirm = ''
      this.accountDialogVisible = true
    },

    confirmDeleteAccount() {
      // 判断用户有邮箱还是手机号
      const userEmail = this.$store.getters['user/email']
      const userPhone = this.$store.getters['user/phone']

      // 确认验证信息是否匹配
      let isValid = false

      if (userEmail) {
        // 如果有邮箱，验证邮箱是否匹配
        isValid = this.deleteEmailConfirm === userEmail
        if (!isValid) {
          this.$message.error(this.$t('views.account_center.email_incorrect'))
          return
        }
      } else if (userPhone) {
        // 如果没有邮箱但有手机号，验证手机号是否匹配
        isValid = this.deleteEmailConfirm === userPhone
        if (!isValid) {
          this.$message.error(this.$t('views.account_center.phone_incorrect'))
          return
        }
      } else {
        this.$message.error(this.$t('views.account_center.identity_verification'))
        return
      }

      this.deleteAccountLoading = true

      // 构建删除账号参数
      const params = {}
      if (userEmail) {
        params.email = this.deleteEmailConfirm
      } else if (userPhone) {
        params.phone = this.deleteEmailConfirm
      }

      deleteAccount(params)
        .then(response => {
          if (response && response.code === 0) {
            this.$message.success(this.$t('views.account_center.account_deleted'))
            // 跳转到登录页面
            window.location.reload()
          } else {
            this.$message.error(response.message || this.$t('views.account_center.delete_account_failed'))
          }
        })
        .catch(error => {
          this.$message.error(error.message || this.$t('views.account_center.delete_account_failed'))
        })
        .finally(() => {
          this.deleteAccountLoading = false
          this.accountDialogVisible = false
        })
    },
    getDeviceList() {
      this.isLoading = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      getUserDevice(params)
        .then(response => {
          if (response) {
            // 获取当前设备ID
            const currentDeviceId = localStorage.getItem('X-Device-ID')
            // 对设备列表进行排序，将当前设备置顶
            this.deviceList = response.data.data.sort((a, b) => {
              if (a.device_id === currentDeviceId) return -1
              if (b.device_id === currentDeviceId) return 1
              return 0
            })
            this.total = response.data.total
          }
        })
        .catch(error => {
          this.$message.error(error.message || this.$t('views.account_center.get_device_list_failed'))
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/css/variables';
</style>

<style scoped lang="scss" >
@import '@/assets/css/views/account_center/components/account_settings';
</style>

